import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Divider, Typography, useMediaQuery, useTheme, Stack } from "@mui/material";
/**
 * Component to render a subtitle or maintain layout spacing if no subtitle is present
 * @param subtitle - Optional subtitle to display
 * @returns The subtitle if provided
 */
const SubtitleSection = ({ subtitle }) => {
    return _jsx(Typography, Object.assign({ variant: "body1" }, { children: subtitle }));
};
/**
 * This component is used to render the title and subtitle of the checkout page. It is
 * a copy of the account page title component rather than reusing the account page title
 * to avoid any future changes to the account page title component affecting the checkout.
 *
 * @note This component renders the title, subtitle, and children in the following order:
 * * For mobile view, a column layout is used.
 * * Row 1. Title
 * * Row 2. Subtitle
 * * Row 3. Children
 *
 * * For desktop view, a mixed column/row layout is used.
 * * Row 1 (left) Title, (right) Children
 * * Row 2. Subtitle
 *
 * * The effect of this layout is that the subtitle is always displayed below the title.
 *
 * @param title - Title of the page
 * @param children - Component to display next to the title. (e.g. buttons, selects, etc.)
 * @returns
 */
export const CheckoutPageTitle = ({ title, subtitle, children, dividerVariant = "fullWidth", }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    return (_jsxs(Box, Object.assign({ id: "checkout-page-title", height: "100%", width: "100%" }, { children: [_jsxs(Stack, Object.assign({ flexWrap: "wrap", mb: isMobile ? 0 : 2, rowGap: isMobile ? 2 : 0, flexGrow: 1 }, { children: [_jsxs(Stack, Object.assign({ flexDirection: !isMobile ? "row" : "column", flexWrap: "wrap", justifyContent: "space-between", alignItems: !isMobile ? "center" : "stretch", flexGrow: 1, rowGap: isMobile ? 0 : 2 }, { children: [_jsx(Typography, Object.assign({ variant: "h1" }, { children: title })), isMobile ? _jsx(SubtitleSection, Object.assign({}, { subtitle })) : children] })), isMobile ? children : _jsx(SubtitleSection, Object.assign({}, { subtitle }))] })), 
            // Do not show the divider on mobile views.
            !isMobile && _jsx(Divider, { variant: dividerVariant, flexItem: true })] })));
};
